import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, UserCheck, AlertTriangle, ArrowRight, Users } from "lucide-react";
import { verifyPass, checkinPass, hasToken } from "@/lib/api";
import { LOGO_ICON, BRAND } from "@/lib/contact";

export default function VerifyPass() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | done
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!hasToken()) {
      navigate(`/admin/login?next=/verify/${code}`, { replace: true });
      return;
    }
    verifyPass(code)
      .then((d) => { setData(d); setState(d.checked_in ? "done" : "ready"); })
      .catch((e) => {
        if (e?.response?.status === 401) navigate(`/admin/login?next=/verify/${code}`, { replace: true });
        else setState("notfound");
      });
  }, [code, navigate]);

  const doCheckin = async () => {
    setChecking(true);
    try {
      const res = await checkinPass(code);
      if (res.already_checked_in) toast.info("سبق تسجيل دخول هذه البطاقة");
      else toast.success("تم تسجيل الدخول بنجاح");
      setState("done");
    } catch { toast.error("تعذّر تسجيل الدخول"); }
    finally { setChecking(false); }
  };

  const Wrap = ({ children }) => (
    <div className="min-h-screen gradient-primary grain flex items-center justify-center px-5" dir="rtl">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src={LOGO_ICON} alt={BRAND} className="w-10 h-10 rounded-lg object-cover ring-1 ring-gold/50" />
          <span className="text-white font-serif text-2xl">{BRAND}</span>
        </div>
        {children}
        <button onClick={() => navigate("/admin")} className="mt-6 mx-auto flex items-center gap-2 text-white/70 hover:text-gold transition text-sm" data-testid="back-to-admin-btn">
          <ArrowRight className="w-4 h-4" /> رجوع للوحة التحكم
        </button>
      </motion.div>
    </div>
  );

  if (state === "loading") return <div className="min-h-screen gradient-primary flex items-center justify-center text-gold font-serif text-2xl">جارٍ التحقق...</div>;

  if (state === "notfound") return (
    <Wrap>
      <div className="rounded-3xl bg-white p-8 text-center" data-testid="verify-notfound">
        <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-8 h-8" /></div>
        <h2 className="font-serif text-2xl font-bold" style={{ color: "#232a44" }}>بطاقة غير صالحة</h2>
        <p className="text-sm mt-2" style={{ color: "#6c7189" }}>الرمز غير موجود أو غير صحيح.</p>
      </div>
    </Wrap>
  );

  const r = data.rsvp, inv = data.invitation;

  return (
    <Wrap>
      <div className="rounded-3xl bg-white overflow-hidden" data-testid="verify-card">
        <div className={`p-6 text-center text-white ${state === "done" ? "bg-emerald-600" : "bg-emerald-500"}`} style={{ background: state === "done" ? "#0f766e" : "#16a34a" }}>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            {state === "done" ? <UserCheck className="w-8 h-8" /> : <Check className="w-8 h-8" />}
          </div>
          <h2 className="font-serif text-2xl font-bold" data-testid="verify-status">{state === "done" ? "تم الدخول" : "بطاقة صالحة"}</h2>
        </div>
        <div className="p-7 text-center" style={{ color: "#232a44" }}>
          <div className="text-xs mb-1" style={{ color: "#6c7189" }}>الضيف</div>
          <div className="font-serif text-2xl font-bold mb-4" data-testid="verify-guest-name">{r.guest_name}</div>
          <div className="inline-flex items-center gap-2 rounded-full bg-lavender px-5 py-2 mb-5" style={{ color: "#3a4a78" }}>
            <Users className="w-4 h-4" /> عدد المدعوين: <b data-testid="verify-guest-count">{r.guests_count}</b>
          </div>
          {inv && (
            <div className="text-sm border-t border-lav pt-4" style={{ color: "#6c7189" }}>
              <div className="font-semibold" style={{ color: "#232a44" }}>{inv.title}</div>
              {inv.event_date && <div className="mt-1">{new Date(inv.event_date).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" })}</div>}
              {inv.venue_name && <div>{inv.venue_name}</div>}
            </div>
          )}
          <div className="mt-3 text-xs tracking-widest" style={{ color: "#9aa0b4" }}>رمز البطاقة: {code}</div>

          {state === "ready" ? (
            <button onClick={doCheckin} disabled={checking} className="w-full mt-6 rounded-xl bg-emerald-500 text-white py-4 font-bold disabled:opacity-60" style={{ background: "#16a34a" }} data-testid="checkin-btn">
              {checking ? "جارٍ التسجيل..." : "تأكيد الدخول"}
            </button>
          ) : (
            <div className="w-full mt-6 rounded-xl bg-emerald-50 text-emerald-700 py-4 font-semibold flex items-center justify-center gap-2" data-testid="already-checked">
              <UserCheck className="w-5 h-5" /> تم تسجيل الدخول مسبقًا
            </div>
          )}
        </div>
      </div>
    </Wrap>
  );
}
